import React, { useEffect, useState } from 'react';

import { showToast } from '@/src/globalFunction';
import {
  useDeleteProjectTag,
  useDetachLibraryFolderTag,
  useGetProjectTags,
  useUpdateProjectTag,
  invalidateFilesQuery,
  invalidateLibraryQueries,
} from './api/projectTags';
import AssignedTags from './Elements/AssignedTags';
import ConfirmTagActionModal from './Elements/ConfirmTagActionModal';
import TagEntry from './Tag';
import { TagType } from './types';
import { useHasFullTagsAccess } from './utils';

import { useLibraryTagsStore } from '../Library/store/libraryTags.store';
import { useGetProjectPermissions } from '../api/getProjectPermissions';

interface LibraryTagsManagerProps {
  inboxDocumentId: string | number;
  projectId: string | number;
  refetchFiles?: () => void;
  folderId?: string;
  style?: React.CSSProperties;
  fullAccessPermissions?: boolean;
  assignedTags?: TagType[];
  setAssignedTags?: (tags: TagType[]) => void;
}

const LibraryTagsManager: React.FC<LibraryTagsManagerProps> = ({
  inboxDocumentId,
  projectId,
  refetchFiles,
  folderId,
  style,
  assignedTags,
  setAssignedTags,
}) => {
  const { updateFolderTags, setTags } = useLibraryTagsStore((state) => state);

  const { data: projectTags, refetch: refetchProjectTags } = useGetProjectTags(projectId, {
    enabled: false,
    keepPreviousData: true,
  });
  const deleteProjectTagMutation = useDeleteProjectTag(Number(projectId));
  const updateProjectTagMutation = useUpdateProjectTag(projectId);
  const detachLibraryTagMutation = useDetachLibraryFolderTag(inboxDocumentId as number);

  const [editState, setEditState] = useState<{
    openId: string | null;
    editTagIndex: number;
  }>({ openId: null, editTagIndex: -1 });
  const [showConfirmModal, setShowConfirmModal] = useState<null | number>(null);
  const [showUpdateConfirmModal, setShowUpdateConfirmModal] = useState<null | number>(null);
  const [pendingUpdate, setPendingUpdate] = useState<{
    tagIndex: number;
    updatedTag: TagType;
  } | null>(null);

  const libraryPermissionsQuery = useGetProjectPermissions({ projectId: +projectId });
  const hasLibraryTagsAccess = useHasFullTagsAccess(
    libraryPermissionsQuery.data?.data.library?.actions
  );

  // Sync global store with latest projectTags
  useEffect(() => {
    if (Array.isArray(projectTags)) {
      setTags(projectTags);
    }
  }, [projectTags, setTags]);

  // Handler for edit/save: show update confirmation modal and store pending update
  const handleEditTagSave = (tagIndex: number, updatedTag: TagType) => {
    setPendingUpdate({ tagIndex, updatedTag });
    setShowUpdateConfirmModal(tagIndex);
  };

  // Confirm update handler
  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      handleRequestUpdate(pendingUpdate.tagIndex, pendingUpdate.updatedTag);
    }
    setShowUpdateConfirmModal(null);
    setPendingUpdate(null);
  };
  // Handler for delete: show delete confirmation modal
  const handleRemoveTag = (tagIndex: number) => {
    setShowConfirmModal(tagIndex);
  };

  // Confirm delete handler
  const handleConfirmDelete = () => {
    if (showConfirmModal === null) return;
    const tag = assignedTags[showConfirmModal];
    if (!tag) return;
    setShowConfirmModal(null);
    deleteProjectTagMutation.mutate(
      { tagId: Number(tag.id), version: tag.version },
      {
        onSuccess: () => {
          const newTags = assignedTags.filter((_, idx) => idx !== showConfirmModal);
          updateFolderTags(folderId, newTags);
          setTags((projectTags || []).filter((t) => t.id !== tag.id));
          setEditState({ openId: null, editTagIndex: null });
        },
      }
    );
  };

  const handleEditTag = (tagIndex: number) =>
    setEditState({ openId: folderId, editTagIndex: tagIndex });

  // Handler for tag update
  const handleRequestUpdate = (tagIndex: number, updatedTag: TagType) => {
    if (!projectId) return;
    updateProjectTagMutation.mutate(
      {
        tagId: updatedTag.id,
        body: { name: updatedTag.name, color: updatedTag.color, version: updatedTag.version },
      },
      {
        onSuccess: (updatedTagFromServer) => {
          const newTags = assignedTags.map((tag, idx) =>
            idx === tagIndex ? { ...tag, ...updatedTagFromServer } : tag
          );
          updateFolderTags(folderId, newTags);
          // If all tags are updated, update assignedTags prop for instant UI
          if (setAssignedTags) {
            setAssignedTags(newTags);
          }
          setEditState({ openId: null, editTagIndex: -1 });
          showToast('success', 'Tag updated successfully');
          invalidateLibraryQueries();
          invalidateFilesQuery();
          setShowUpdateConfirmModal(null);
          setPendingUpdate(null);
        },
      }
    );
  };

  // Unassign tag from folder only (remove from assignedTags for this folder)
  const handleUnassignTag = (tagIndex: number) => {
    const tag = assignedTags[tagIndex];
    if (!tag || !inboxDocumentId) return;
    detachLibraryTagMutation.mutate(Number(tag.id), {
      onSuccess: () => {
        const newTags = assignedTags.filter((_, idx) => idx !== tagIndex);
        updateFolderTags(folderId, newTags);
        if (refetchFiles) refetchFiles();
      },
    });
  };

  return (
    <>
      {assignedTags?.length > 0 ? (
        <>
          <AssignedTags
            assignedTags={assignedTags}
            rowId={folderId}
            onRemoveTag={handleRemoveTag}
            onUnassignTag={handleUnassignTag}
            onEdit={handleEditTag}
            isLibrary={true}
            isTagReadOnly={!hasLibraryTagsAccess}
            style={style}
          />
          {editState.openId === folderId && editState.editTagIndex !== null && (
            <TagEntry
              id={folderId}
              openId={editState.openId}
              setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
              assignedTags={assignedTags}
              editTagIndex={editState.editTagIndex}
              setEditTagIndex={(idx) => setEditState((prev) => ({ ...prev, editTagIndex: idx }))}
              allProjectTags={projectTags}
              inboxDocumentId={inboxDocumentId}
              isLibrary={true}
              onRequestUpdate={handleRequestUpdate}
              isTagReadOnly={!hasLibraryTagsAccess}
              onSave={handleEditTagSave}
              setAssignedTags={setAssignedTags}
            />
          )}
        </>
      ) : (
        <TagEntry
          id={folderId}
          openId={editState.openId}
          setOpenId={(id) => setEditState((prev) => ({ ...prev, openId: id }))}
          assignedTags={assignedTags}
          editTagIndex={editState.editTagIndex}
          setEditTagIndex={(idx) => setEditState((prev) => ({ ...prev, editTagIndex: idx }))}
          allProjectTags={projectTags}
          inboxDocumentId={inboxDocumentId}
          onRequestUpdate={handleRequestUpdate}
          isTagReadOnly={!hasLibraryTagsAccess}
          isLibrary={true}
          setAssignedTags={setAssignedTags}
        />
      )}
      {showConfirmModal !== null && (
        <ConfirmTagActionModal
          onClose={() => setShowConfirmModal(null)}
          onSubmit={handleConfirmDelete}
          subject={'Tag'}
          actionType="delete"
        />
      )}
      {showUpdateConfirmModal !== null && (
        <ConfirmTagActionModal
          onClose={() => {
            setShowUpdateConfirmModal(null);
            setPendingUpdate(null);
          }}
          onSubmit={handleConfirmUpdate}
          subject={'Tag'}
          actionType="update"
        />
      )}
    </>
  );
};

export default LibraryTagsManager;
